import { memo, useCallback, type ChangeEvent, type FC } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../hooks";
import type { RankingsSlice } from "./slice";

const OPTIONS: Array<{
  key: keyof RankingsSlice;
  label: string;
  testId: string;
}> = [
  {
    key: "fortune500",
    label: "Fortune 500",
    testId: "rankings-fortune500-checkbox-btn",
  },
  {
    key: "forbes2000",
    label: "Forbes Global 2000",
    testId: "rankings-forbes2000-checkbox-btn",
  },
];

export const RankingsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("rankings") as RankingsSlice;
  const { setSlice } = useCompanyFilterActions();

  const handleChange = useCallback(
    (key: keyof RankingsSlice) => (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setSlice("rankings", (prev) => ({ ...prev, [key]: checked }));
    },
    [setSlice]
  );

  return (
    <Stack spacing={1} data-testid="rankings-filter">
      <Typography variant="body2" color="text.secondary">
        Only show companies that are themselves ranked in the selected lists.
      </Typography>
      {OPTIONS.map(({ key, label, testId }) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={slice[key]}
              onChange={handleChange(key)}
              size="small"
              data-testid={testId}
            />
          }
          label={<Typography variant="body2">{label}</Typography>}
        />
      ))}
    </Stack>
  );
});

RankingsFilter.displayName = "RankingsFilter";
